import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { MoreVertical } from 'lucide-react';
import { RsvpStatsSection } from '../guests/RsvpStatsSection';
import { mockRsvpTotals, mockRecentResponses, mockGuests } from './sampleData';

const noop = () => {};

const mockTableGuests = [
  { name: 'Emily Watson', email: 'emily@example.com', rsvp: 'Attending', rsvpColor: 'bg-success/10 text-success', views: 5, status: 'Invited 1 Jan', statusColor: 'bg-primary/10 text-primary', tags: [{ label: 'Day Guest', color: 'bg-sky-500/15 text-text-primary' }, { label: 'Family', color: 'bg-purple-500/15 text-text-primary' }], plus: 1, notes: 'Vegetarian' },
  { name: 'David Chen', email: 'david@example.com', rsvp: 'Attending', rsvpColor: 'bg-success/10 text-success', views: 3, status: 'Invited 1 Jan', statusColor: 'bg-primary/10 text-primary', tags: [{ label: 'Day Guest', color: 'bg-sky-500/15 text-text-primary' }, { label: 'Friends', color: 'bg-amber-500/15 text-text-primary' }], plus: 0, notes: '' },
  { name: 'Sarah Miller', email: 'sarah@example.com', rsvp: 'Maybe', rsvpColor: 'bg-warning/10 text-warning', views: 2, status: 'Invited 1 Jan', statusColor: 'bg-primary/10 text-primary', tags: [{ label: 'Evening', color: 'bg-pink-500/15 text-text-primary' }, { label: 'Friends', color: 'bg-amber-500/15 text-text-primary' }], plus: 1, notes: '' },
  { name: 'Tom Brown', email: 'tom@example.com', rsvp: 'Awaiting', rsvpColor: 'bg-background-hover text-text-muted', views: 1, status: 'Invited 1 Jan', statusColor: 'bg-primary/10 text-primary', tags: [{ label: 'Day Guest', color: 'bg-sky-500/15 text-text-primary' }, { label: 'Bridal Party', color: 'bg-red-500/15 text-text-primary' }], plus: 0, notes: '' },
  { name: 'Lisa Park', email: 'lisa@example.com', rsvp: 'Awaiting', rsvpColor: 'bg-background-hover text-text-muted', views: 0, status: 'Not invited', statusColor: 'bg-background-hover text-text-muted', tags: [{ label: 'Evening', color: 'bg-pink-500/15 text-text-primary' }, { label: 'Family', color: 'bg-purple-500/15 text-text-primary' }], plus: 0, notes: '' },
];

function MockGuestTable() {
  return (
    <motion.div
      className="mt-4 border border-border rounded-lg overflow-hidden bg-background"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-background-card border-b border-border text-text-secondary">
            <th className="w-8 px-3 py-2.5"><div className="w-3.5 h-3.5 rounded border border-border" /></th>
            <th className="text-left px-3 py-2.5 font-medium">Guest</th>
            <th className="text-left px-3 py-2.5 font-medium">RSVP</th>
            <th className="text-left px-3 py-2.5 font-medium">Views</th>
            <th className="text-left px-3 py-2.5 font-medium">Status</th>
            <th className="text-left px-3 py-2.5 font-medium">Tags</th>
            <th className="text-left px-3 py-2.5 font-medium">+1</th>
            <th className="text-left px-3 py-2.5 font-medium">Notes</th>
            <th className="w-8 px-3 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {mockTableGuests.map((g, i) => (
            <motion.tr
              key={i}
              className="border-b border-border last:border-0 bg-white"
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.5 + i * 0.1 }}
            >
              <td className="px-3 py-2.5"><div className="w-3.5 h-3.5 rounded border border-border" /></td>
              <td className="px-3 py-2.5">
                <div className="font-medium text-text-primary">{g.name}</div>
                <div className="text-text-muted text-[10px]">{g.email}</div>
              </td>
              <td className="px-3 py-2.5">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${g.rsvpColor}`}>{g.rsvp}</span>
              </td>
              <td className="px-3 py-2.5 text-text-secondary">{g.views}</td>
              <td className="px-3 py-2.5">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${g.statusColor}`}>{g.status}</span>
              </td>
              <td className="px-3 py-2.5">
                <div className="flex gap-1 flex-wrap">
                  {g.tags.map(t => (
                    <span key={t.label} className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${t.color}`}>{t.label}</span>
                  ))}
                </div>
              </td>
              <td className="px-3 py-2.5 text-text-secondary">{g.plus || '—'}</td>
              <td className="px-3 py-2.5 text-text-secondary truncate max-w-[80px]">{g.notes || '—'}</td>
              <td className="px-3 py-2.5"><MoreVertical size={12} className="text-text-muted" /></td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  );
}

function GuestManagementCard({ scale, baseWidth, withShadow = true }: { scale: number; baseWidth: number; withShadow?: boolean }) {
  return (
    <div
      className={`relative bg-background rounded-2xl border border-border overflow-hidden mx-auto ${withShadow ? 'shadow-xl' : ''}`}
      style={{ width: baseWidth * scale, height: 780 * scale }}
    >
      <div
        style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: baseWidth }}
      >
        <div className="flex items-center gap-2 px-4 py-2.5 bg-background-card border-b border-border">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
          </div>
          <div className="flex-1 mx-4">
            <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-400 text-center max-w-xs mx-auto">
              wedsy.co/dashboard/guests
            </div>
          </div>
        </div>

        <style>{`.guest-showcase .overflow-y-auto, .guest-showcase .overflow-x-auto { overflow: hidden !important; max-height: none !important; }`}</style>
        <div className="guest-showcase p-4 overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <RsvpStatsSection
              totals={mockRsvpTotals}
              recentResponses={mockRecentResponses}
              guests={mockGuests}
              onSendReminder={noop}
              onNudge={noop}
              onSendToUninvited={noop}
              onMarkAsInvited={noop}
              isSending={false}
              isAdmin={false}
            />
          </motion.div>
          <MockGuestTable />
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
}

export function GuestManagementShowcase() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.55);
  const baseWidth = 800;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const available = el.clientWidth;
      setScale(Math.min(available / baseWidth, 0.65));
    };
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={containerRef}>
      {/* Desktop: with perspective */}
      <div className="hidden md:block" style={{ perspective: '1000px' }}>
        <motion.div
          initial={{ rotateY: 0 }}
          whileInView={{ rotateY: -6, rotateX: 3 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="pointer-events-none"
        >
          <GuestManagementCard scale={scale} baseWidth={baseWidth} />
        </motion.div>
      </div>
      {/* Mobile: flat, no perspective, no shadow */}
      <div className="md:hidden pointer-events-none">
        <GuestManagementCard scale={scale} baseWidth={baseWidth} withShadow={false} />
      </div>
    </div>
  );
}
